import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDemoVideo, saveOrUpdateDemoVideo, deleteDemoVideo } from "../../redux/actions/demoVideoAction";
import { customConfirmPopup } from "../../utils/commonFunctions";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Card } from "primereact/card";


const DemoVideo = () => {
    const dispatch = useDispatch();
    const demoVideo = useSelector((state) => state.demoVideo.demoVideo);

    const [title, setTitle] = useState("Demo Video");
    const [description, setDescription] = useState("");
    const [videoLink, setVideoLink] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        dispatch(getDemoVideo());
    }, [dispatch]);

    useEffect(() => {
        if (demoVideo) {
            setTitle(demoVideo.title || "Demo Video");
            setDescription(demoVideo.description || "");
            setVideoLink(demoVideo.videoUrl || "");
        }
    }, [demoVideo]);

    const onSubmit = () => {
        if (!videoLink.trim()) return;

        const data = {
            title,
            description,
            video: [],
            thumbnail: [],
            videoUrl: videoLink.trim(),
            thumbnailUrl: "",
        };

        dispatch(saveOrUpdateDemoVideo(data, dispatch, setLoading));
    };

    const onDelete = () => {
        customConfirmPopup(
            () => dispatch(deleteDemoVideo()),
            "Are you sure you want to delete this demo video?"
        );
    };

    const getYouTubeEmbedUrl = (url) => {
        if (!url) return null;
        let videoId = null;
        // youtube.com/watch?v=ID
        const watchMatch = url.match(/[?&]v=([^&#]+)/);
        if (watchMatch) videoId = watchMatch[1];
        // youtu.be/ID
        if (!videoId) {
            const shortMatch = url.match(/youtu\.be\/([^?&#]+)/);
            if (shortMatch) videoId = shortMatch[1];
        }
        // youtube.com/embed/ID
        if (!videoId) {
            const embedMatch = url.match(/youtube\.com\/embed\/([^?&#]+)/);
            if (embedMatch) videoId = embedMatch[1];
        }
        return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    };

    const isYouTubeUrl = (url) => {
        return url && (url.includes("youtube.com") || url.includes("youtu.be"));
    };

    const renderVideoPreview = (url) => {
        const embedUrl = getYouTubeEmbedUrl(url);
        if (embedUrl) {
            return (
                <iframe
                    src={embedUrl}
                    title="Demo Video Preview"
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    style={{
                        width: "100%",
                        height: "450px",
                        borderRadius: "8px",
                        border: "none",
                    }}
                />
            );
        }
        return (
            <video
                src={url}
                controls
                style={{
                    width: "100%",
                    maxHeight: "450px",
                    borderRadius: "8px",
                    backgroundColor: "#000",
                }}
            >
                Your browser does not support the video tag.
            </video>
        );
    };

    return (
        <div className="grid">
            <div className="col-12">
                <h3>
                    <i className="pi pi-video mr-2" />
                    Demo Video Management
                </h3>
                <p className="text-color-secondary">
                    Add or update the demo video link that will be shown in the app's Demo section.
                </p>
            </div>

            {/* Current Video Preview */}
            {demoVideo?.videoUrl && (
                <div className="col-12">
                    <Card title="Current Demo Video" className="mb-4">
                        <div className="mb-3">
                            {renderVideoPreview(demoVideo.videoUrl)}
                        </div>
                        <div className="grid">
                            <div className="col-12 md:col-6">
                                <p>
                                    <strong>Title:</strong> {demoVideo.title}
                                </p>
                            </div>
                            <div className="col-12 md:col-6">
                                <p>
                                    <strong>Updated:</strong>{" "}
                                    {new Date(demoVideo.time).toLocaleString()}
                                </p>
                            </div>
                            {demoVideo.description && (
                                <div className="col-12">
                                    <p>
                                        <strong>Description:</strong>{" "}
                                        {demoVideo.description}
                                    </p>
                                </div>
                            )}
                            <div className="col-12">
                                <p>
                                    <strong>Video URL:</strong>{" "}
                                    <a href={demoVideo.videoUrl} target="_blank" rel="noopener noreferrer">
                                        {demoVideo.videoUrl}
                                    </a>
                                </p>
                            </div>
                        </div>
                    </Card>
                </div>
            )}

            {/* Form */}
            <div className="col-12">
                <Card
                    title={demoVideo?.videoUrl ? "Update Demo Video" : "Add Demo Video"}
                >
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="demo-video-title" className="font-bold block mb-2">
                                    Title
                                </label>
                                <InputText
                                    id="demo-video-title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    className="w-full"
                                    placeholder="Enter video title"
                                />
                            </div>
                        </div>
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label htmlFor="demo-video-description" className="font-bold block mb-2">
                                    Description
                                </label>
                                <InputTextarea
                                    id="demo-video-description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="w-full"
                                    rows={1}
                                    autoResize
                                    placeholder="Enter video description"
                                />
                            </div>
                        </div>

                        <div className="col-12">
                            <div className="field">
                                <label htmlFor="demo-video-link" className="font-bold block mb-2">
                                    Video URL *
                                </label>
                                <InputText
                                    id="demo-video-link"
                                    value={videoLink}
                                    onChange={(e) => setVideoLink(e.target.value)}
                                    className="w-full"
                                    placeholder="Paste video URL (YouTube link or direct .mp4 link)"
                                />
                                {!videoLink.trim() && (
                                    <small className="p-error block mt-1">
                                        Video URL is required
                                    </small>
                                )}
                            </div>
                        </div>

                        <div className="col-12">
                            <Button
                                label={
                                    loading
                                        ? "Saving..."
                                        : demoVideo?.videoUrl
                                        ? "Update Demo Video"
                                        : "Save Demo Video"
                                }
                                icon={loading ? "pi pi-spin pi-spinner" : "pi pi-save"}
                                className="bg-main mr-2"
                                onClick={onSubmit}
                                disabled={loading || !videoLink.trim()}
                            />
                            {demoVideo?.videoUrl && (
                                <Button
                                    label="Delete Video"
                                    icon="pi pi-trash"
                                    className="p-button-danger"
                                    onClick={onDelete}
                                    disabled={loading}
                                />
                            )}
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default DemoVideo;
